import { useEffect, useMemo, useRef, useState } from 'react';
import type { EstimateAlgorithm, Floor } from '@wrp/shared';
import * as api from '../api';
import { useDialogs } from '../components/Dialogs';
import { FloorMap } from '../components/FloorMap';
import type { MapSample } from '../components/FloorMap';
import { Icon, IconButton } from '../components/Icon';
import { formatTime } from '../format';
import { useApp } from '../store';
import { downloadJson } from './evaluation';
import { buildMonitoringFile, defaultTitle, fileNameFor, parseMonitoringFile } from './monitoring';
import type { MonitorEntry, MonitorState } from './monitoring';
import { ALGORITHMS, useRecordedReportIds } from './shared';

const CONCURRENCY = 4;

interface MonitoringModeProps {
  floor: Floor;
  state: MonitorState;
  onChange: (update: (prev: MonitorState) => MonitorState) => void;
}

export function MonitoringMode({ floor, state, onChange }: MonitoringModeProps) {
  const { referencePoints, fingerprints, fingerprintCountByRefPoint, pushToast } = useApp();
  const dialogs = useDialogs();
  const recorded = useRecordedReportIds();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { title, running, startedAt, algorithm, k, entries } = state;
  const floorPoints = useMemo(() => referencePoints.filter((rp) => rp.floor_id === floor.id), [referencePoints, floor.id]);
  const fingerprintByReport = useMemo(() => new Map(fingerprints.map((fp) => [fp.report_id, fp])), [fingerprints]);

  useEffect(() => {
    if (title === '') onChange((prev) => (prev.title === '' ? { ...prev, title: defaultTitle() } : prev));
  }, [title, onChange]);

  // ------------------------------------------------------------- estimation
  // Estimates depend on the algorithm settings and on the fingerprint database.
  const key = `${algorithm}|${k}|${fingerprints.length}`;
  const keyRef = useRef(key);
  keyRef.current = key;
  const inFlight = useRef(new Set<string>());

  useEffect(() => {
    const todo = Object.values(entries).filter(
      (e) => e.label === null && !e.frozen && e.key !== key && !inFlight.current.has(`${e.report_id}|${key}`),
    );
    if (todo.length === 0) return;

    const estimateOne = async (entry: MonitorEntry) => {
      const tag = `${entry.report_id}|${key}`;
      inFlight.current.add(tag);
      try {
        const result = await api.estimate({ report_id: entry.report_id, algorithm, k, floor_id: floor.id, source: 'scan' });
        if (keyRef.current !== key) return;
        onChange((prev) => {
          const existing = prev.entries[entry.report_id];
          if (!existing || existing.frozen) return prev;
          const next: MonitorEntry = {
            ...existing,
            estimate: result.position ? { x: result.position.x, y: result.position.y } : null,
            matched_aps: result.matched_aps,
            query_aps: result.query_aps,
            key,
          };
          return { ...prev, entries: { ...prev.entries, [entry.report_id]: next } };
        });
      } catch {
        // Retried on the next change of the session or settings.
      } finally {
        inFlight.current.delete(tag);
      }
    };

    const queue = [...todo];
    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) await estimateOne(next);
    };
    for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) void worker();
  }, [entries, key, algorithm, k, floor.id, onChange]);

  // ----------------------------------------------------------------- entries
  const list = useMemo(
    () => Object.values(entries).sort((a, b) => b.received_at.localeCompare(a.received_at)),
    [entries],
  );
  const located = list.filter((e) => e.estimate).length;
  const mapSamples = useMemo<MapSample[]>(
    () => list.flatMap((e) => (e.estimate && e.label === null ? [{ id: e.report_id, estimate: e.estimate, actual: null }] : [])),
    [list],
  );
  const latest = list.find((e) => e.estimate && e.label === null) ?? null;

  // ---------------------------------------------------------------- session
  const start = async () => {
    if (list.length > 0 && !(await dialogs.confirm('Start a new session? All previous results will be cleared.'))) return;
    setSelectedId(null);
    onChange((prev) => ({
      ...prev,
      title: prev.titleEdited ? prev.title : defaultTitle(),
      running: true,
      startedAt: new Date().toISOString(),
      entries: {},
    }));
  };

  const stop = () => onChange((prev) => ({ ...prev, running: false }));

  const save = () => {
    if (list.length === 0) {
      pushToast('info', 'Nothing to save yet');
      return;
    }
    const filename = fileNameFor(title);
    downloadJson(filename, buildMonitoringFile(floor, state, list));
    pushToast('success', `Saved ${list.length} reports to ${filename}`);
  };

  const load = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = parseMonitoringFile(await file.text());
      if (list.length > 0 && !(await dialogs.confirm('Replace the current results with the loaded session?'))) return;
      setSelectedId(null);
      onChange((prev) => ({
        ...prev,
        title: parsed.title || file.name.replace(/\.json$/i, ''),
        titleEdited: true,
        running: false,
        startedAt: parsed.startedAt,
        algorithm: parsed.algorithm ?? prev.algorithm,
        k: parsed.k ?? prev.k,
        entries: Object.fromEntries(parsed.entries.map((e) => [e.report_id, e])),
      }));
      const note = parsed.floorId && parsed.floorId !== floor.id ? ` (saved for floor "${parsed.floorName}")` : '';
      pushToast('success', `Loaded ${parsed.entries.length} reports${note}`);
    } catch (error) {
      pushToast('error', error instanceof Error ? error.message : 'Failed to load monitoring session');
    }
  };

  return (
    <>
      <div className="live__map">
        <FloorMap
          floor={floor}
          referencePoints={floorPoints}
          fingerprintCountByRefPoint={fingerprintCountByRefPoint}
          samples={mapSamples}
          selectedSampleId={selectedId}
          onSampleClick={setSelectedId}
          hint={running ? null : 'Press start to begin collecting reports'}
        />
      </div>

      <aside className="live__side">
        <section className="box box--auto">
          <header className="box__head">
            <Icon name="list" />
            <h3>Session</h3>
            <span className="box__count">{running ? 'collecting' : startedAt ? 'stopped' : 'not started'}</span>
            <span className="box__spacer" />
            {running ? (
              <IconButton icon="stop" label="Stop collecting reports" danger onClick={stop} />
            ) : (
              <IconButton
                icon="play"
                label={list.length > 0 ? 'Start a new session (clears previous results)' : 'Start collecting reports'}
                primary
                onClick={() => void start()}
              />
            )}
          </header>
          <div className="box__body form-grid">
            <label className="form-grid__wide">
              Title (default file name)
              <input
                type="text"
                value={title}
                maxLength={120}
                onChange={(e) => onChange((prev) => ({ ...prev, title: e.target.value, titleEdited: true }))}
              />
            </label>
            <label>
              Algorithm
              <select value={algorithm} onChange={(e) => onChange((prev) => ({ ...prev, algorithm: e.target.value as EstimateAlgorithm }))}>
                {ALGORITHMS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              k
              <input
                type="number"
                min={1}
                max={50}
                value={k}
                onChange={(e) => onChange((prev) => ({ ...prev, k: Math.max(1, Math.min(50, Number(e.target.value) || 1)) }))}
              />
            </label>
          </div>
          <p className="box__note muted">
            {latest?.estimate ? (
              <>
                Latest: <b>{latest.estimate.x.toFixed(2)}, {latest.estimate.y.toFixed(2)} m</b> at {formatTime(latest.received_at)}
              </>
            ) : startedAt ? (
              `Since ${formatTime(startedAt)} — waiting for scan reports…`
            ) : (
              'Only reports received after Start are kept.'
            )}
          </p>
        </section>

        <section className="box box--grow">
          <header className="box__head">
            <Icon name="list" />
            <h3>Reports</h3>
            <span className="box__count">
              {located}/{list.length} located
            </span>
            <span className="box__spacer" />
            <IconButton icon="download" label={`Save as ${fileNameFor(title)}`} size={14} disabled={list.length === 0} onClick={save} />
            <IconButton icon="upload" label="Load a saved session" size={14} onClick={() => fileInput.current?.click()} />
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                void load(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </header>
          <ul className="box__list">
            {list.length === 0 ? (
              <li className="box__empty">{running ? 'Waiting for scan reports…' : 'No reports. Press start, then send scans from the device.'}</li>
            ) : null}
            {list.map((e) => {
              const fp = fingerprintByReport.get(e.report_id);
              let status: { icon: 'check' | 'close' | 'target' | 'circle'; cls: string; text: string };
              if (fp || recorded.has(e.report_id)) status = { icon: 'check', cls: 'is-done', text: `recorded at ${fp?.label ?? e.label ?? 'point'}` };
              else if (e.label !== null) status = { icon: 'close', cls: 'is-bad', text: `no point labeled "${e.label}"` };
              else if (e.estimate) status = { icon: 'target', cls: 'is-info', text: `${e.estimate.x.toFixed(1)}, ${e.estimate.y.toFixed(1)} m` };
              else if (e.key !== '' || e.frozen) status = { icon: 'close', cls: 'is-bad', text: 'no estimate' };
              else status = { icon: 'circle', cls: 'is-pending', text: 'locating…' };
              return (
                <li
                  key={e.report_id}
                  className={`row${e.report_id === selectedId ? ' is-selected' : ''}`}
                  onClick={() => e.estimate && e.label === null && setSelectedId(e.report_id)}
                >
                  <span className={`row__status ${status.cls}`}>
                    <Icon name={status.icon} size={13} />
                  </span>
                  <span className="row__main">
                    <strong>{status.text}</strong>
                    <span className="muted">
                      {formatTime(e.received_at)} · {e.device_id || '—'} · {e.ap_count} APs
                      {e.matched_aps !== null && e.query_aps !== null ? ` · ${e.matched_aps}/${e.query_aps} matched` : ''}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      </aside>
    </>
  );
}
